import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Users, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCIES = ["One-time", "Monthly", "Annually"];
const AMOUNTS = [50, 100, 250, 500, 1000];

const GetInvolved = () => {
  const [form, setForm] = useState({ name: "", email: "", amount: "", frequency: "One-time", message: "" });
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/donations/stats`);
      setStats(res.data);
    } catch (e) {
      /* stats are decorative */
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.amount) {
      toast.error("Please add your name, email, and pledge amount.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/donations`, {
        name: form.name,
        email: form.email,
        amount: parseFloat(form.amount),
        frequency: form.frequency.toLowerCase(),
        message: form.message || null,
      });
      toast.success(`Thank you, ${form.name.split(" ")[0]}. Your pledge means a front door for a neighbor.`);
      setForm({ name: "", email: "", amount: "", frequency: "One-time", message: "" });
      fetchStats();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section
      id="get-involved"
      data-testid="get-involved-section"
      className="py-28 lg:py-40 bg-secondary/30 border-y border-foreground/10"
    >
      <div className="max-w-[90rem] mx-auto px-6 lg:px-12 grid grid-cols-12 gap-12 lg:gap-20">
        <div className="col-span-12 lg:col-span-5">
          <Reveal>
            <p className="overline-label mb-6" data-testid="get-involved-overline">
              Donate & Get Involved
            </p>
            <h2
              className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight"
              data-testid="get-involved-heading"
            >
              Help us raise{" "}
              <em className="text-primary font-normal">the roof.</em>
            </h2>
            <p className="mt-6 text-base lg:text-lg leading-relaxed text-muted-foreground max-w-md">
              Public funding will carry most of Adam's Way, but the final $5
              million comes from neighbors like you. Pledge your support today
              and our team will reach out to make it official.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-4 group">
                <span className="mt-1 text-primary transition-transform duration-300 group-hover:scale-110"><Heart size={18} /></span>
                <p className="text-sm text-foreground/80 leading-relaxed">
                  <span className="font-medium">Give.</span> Every dollar closes the gap between a blueprint and a front door.
                </p>
              </div>
              <div className="flex items-start gap-4 group">
                <span className="mt-1 text-primary transition-transform duration-300 group-hover:scale-110"><Users size={18} /></span>
                <p className="text-sm text-foreground/80 leading-relaxed">
                  <span className="font-medium">Volunteer & partner.</span> From community events to the future marketplace, there's a place for you.
                </p>
              </div>
            </div>
            {stats && stats.count > 0 && (
              <p className="mt-8 font-serif italic text-lg text-foreground/70" data-testid="pledge-stats">
                ${stats.total.toLocaleString()} pledged by {stats.count}{" "}
                {stats.count === 1 ? "neighbor" : "neighbors"} through this page.
              </p>
            )}
          </Reveal>
        </div>

        <div className="col-span-12 lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="donate-form"
              className="bg-card border border-foreground/10 rounded-3xl p-8 lg:p-12 transition-colors duration-500 hover:border-primary/25"
            >
              <p className="font-serif text-2xl lg:text-3xl font-light mb-8">
                Make a pledge
              </p>

              <div className="flex flex-wrap gap-2 mb-8" data-testid="amount-presets">
                {AMOUNTS.map((a) => (
                  <button
                    type="button"
                    key={a}
                    data-testid={`amount-preset-${a}`}
                    onClick={() => setForm({ ...form, amount: String(a) })}
                    className={`rounded-full px-5 py-2 text-sm font-medium border transition-[background-color,border-color,color,transform] duration-300 hover:-translate-y-0.5 ${
                      form.amount === String(a)
                        ? "bg-primary border-primary text-primary-foreground"
                        : "border-foreground/20 text-foreground/70 hover:border-primary hover:text-primary"
                    }`}
                  >
                    ${a.toLocaleString()}
                  </button>
                ))}
              </div>

              <div className="space-y-6">
                <input
                  type="number"
                  min="1"
                  placeholder="Custom amount (USD)"
                  value={form.amount}
                  onChange={set("amount")}
                  data-testid="donate-amount-input"
                  className="field-input"
                />
                <div className="grid sm:grid-cols-2 gap-6">
                  <input
                    type="text"
                    placeholder="Full name"
                    value={form.name}
                    onChange={set("name")}
                    data-testid="donate-name-input"
                    className="field-input"
                  />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={form.email}
                    onChange={set("email")}
                    data-testid="donate-email-input"
                    className="field-input"
                  />
                </div>
                <div className="flex gap-2" data-testid="frequency-options">
                  {FREQUENCIES.map((f) => (
                    <button
                      type="button"
                      key={f}
                      data-testid={`frequency-${f.toLowerCase()}`}
                      onClick={() => setForm({ ...form, frequency: f })}
                      className={`rounded-full px-4 py-1.5 text-xs font-medium border transition-[background-color,border-color,color] duration-300 ${
                        form.frequency === f
                          ? "bg-foreground border-foreground text-bone"
                          : "border-foreground/20 text-foreground/60 hover:border-foreground/50"
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
                <textarea
                  rows={3}
                  placeholder="A note with your pledge (optional)"
                  value={form.message}
                  onChange={set("message")}
                  data-testid="donate-message-input"
                  className="field-input resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="donate-submit-btn"
                className="mt-10 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 text-sm font-medium text-primary-foreground transition-[background-color,transform] duration-300 hover:bg-lavender-deep hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Sending…" : "Pledge my support"}
              </button>
              <p className="mt-4 text-xs text-muted-foreground text-center">
                TNS Housing Corp is a 501(c)(3) nonprofit. This pledge is an expression of intent; no payment is collected here.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default GetInvolved;
